import * as React from "react";
import { useIsMobile } from "@sproutsocial/seeds-react-hooks";
import { usePanelContext } from "./PanelContext";
import type {
  PanelDirection,
  TypePanelProps,
  TypePanelContext,
} from "./PanelTypes";

/**
 * Class-name merge helper. Copied intentionally rather than imported from
 * `@sproutsocial/seeds-react-utilities` — `cn` is not exported there, so each
 * migrated Tailwind component carries its own copy (mirrors AvatarTailwind /
 * DrawerShared). Because Panel is a multi-part component, the helper lives once
 * here and is imported by every Tailwind sub-component instead of being
 * duplicated in each file.
 */
export function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

/**
 * Dynamic geometry for the outer `<aside>` on the Tailwind path. These values
 * depend on runtime props (open state, width, gap, direction) and are asserted
 * as computed styles by the jsdom tests, so they must be inline `style` rather
 * than CSS-variable-driven classes (panel.css is not loaded under jsdom). The
 * static chrome (background, radius, transition, overflow, flex-grow/shrink)
 * lives in panel.css.
 *
 * The gap sits on the margin edge facing the adjacent content: left when the
 * panel anchors right, right when it anchors left, top when it anchors to the
 * bottom (mirrors `gapSide` in styles.ts).
 */
export const getPanelContainerStyle = (
  direction: PanelDirection,
  width: number,
  gap: number,
  isPanelOpen: boolean
): React.CSSProperties => {
  const gapValue = isPanelOpen && gap ? `${gap}px` : "0px";
  const style: React.CSSProperties = {
    flexBasis: isPanelOpen ? `${width}px` : "0px",
  };

  if (direction === "bottom") {
    style.width = "100%";
  } else {
    style.alignSelf = "stretch";
  }

  if (direction === "right") {
    style.marginLeft = gapValue;
  } else if (direction === "left") {
    style.marginRight = gapValue;
  } else {
    style.marginTop = gapValue;
  }

  return style;
};

/**
 * Dynamic geometry for the inner layout `<div>` on the Tailwind path. The
 * direction-dependent min-width/min-height come from the `width` prop.
 */
export const getPanelInnerStyle = (
  direction: PanelDirection,
  width: number
): React.CSSProperties =>
  direction === "bottom"
    ? { minHeight: `${width}px`, width: "100%" }
    : { minWidth: `${width}px`, height: "100%" };

/**
 * Shared render logic for both the styled and the Tailwind shells: reads the
 * PanelProvider context, enriches it (closeButtonLabel/headerless), freezes the
 * last-rendered children during the collapse, resolves the mobile breakpoint,
 * and computes the auto-wired accessible name. Centralising it here guarantees
 * the two shells stay behaviourally identical — only the desktop container
 * markup differs between them.
 */
export const usePanelShell = (props: TypePanelProps) => {
  const {
    children,
    closeButtonLabel,
    header,
    headerless = false,
    footer,
    title,
    titleId,
    direction = "right",
    width = 384,
    gap = 4,
    mobileBreakpoint,
    zIndex,
    id,
    snapPoints,
    defaultSnapPoint,
    snapPoint,
    onSnapPointChange,
    snapToSequentialPoints,
    actions,
    actionsInHeader,
    "aria-labelledby": ariaLabelledByProp,
    "aria-label": ariaLabel,
    ...rest
  } = props;

  const panelContext = usePanelContext();
  const { isPanelOpen, closePanel } = panelContext;
  const isMobile = useIsMobile(mobileBreakpoint);

  // Expose closeButtonLabel through context so PanelCloseButton can read it
  // without prop drilling — same pattern as DrawerContext.
  const enrichedValue = React.useMemo<TypePanelContext>(
    () => ({ ...panelContext, closeButtonLabel, headerless }),
    [panelContext, closeButtonLabel, headerless]
  );

  // Freeze the last-rendered children during the collapse so consumers that
  // conditionally render content don't produce an empty panel shell while the
  // flex-basis animation runs.
  const prevChildrenRef = React.useRef<React.ReactNode>(null);
  if (isPanelOpen) {
    prevChildrenRef.current = children;
  }
  const renderedChildren = isPanelOpen ? children : prevChildrenRef.current;

  // When the consumer relies on the default header, its title element renders
  // with `id={titleId}`. Auto-wire `aria-labelledby` so the dialog/aside has an
  // accessible name without forcing every caller to repeat the id.
  const effectiveAriaLabelledBy =
    ariaLabelledByProp ?? (!headerless && header == null ? titleId : undefined);

  return {
    rest,
    children,
    closeButtonLabel,
    header,
    headerless,
    footer,
    title,
    titleId,
    direction,
    width,
    gap,
    zIndex,
    id,
    snapPoints,
    defaultSnapPoint,
    snapPoint,
    onSnapPointChange,
    snapToSequentialPoints,
    actions,
    actionsInHeader,
    ariaLabel,
    isPanelOpen,
    closePanel,
    isMobile,
    enrichedValue,
    renderedChildren,
    effectiveAriaLabelledBy,
  };
};

/**
 * The shared shell values consumed by both the styled and the Tailwind desktop
 * shells and by the shared mobile render. Inferred from `usePanelShell` so the
 * `rest` pass-through keeps its precise type (mirrors `TypeDrawerShell`).
 */
export type TypePanelShell = ReturnType<typeof usePanelShell>;
